/**
 * Width-based shim for `react-device-detect` (webpack alias `react-device-detect$`).
 *
 * ## Standard
 * | Need | Use |
 * | --- | --- |
 * | React UI that re-renders on resize | `useIsMobile` / `useIsPhone` / `withResponsive` from `@/hooks` |
 * | One-shot / util read | `getIsMobileOnly()` / `getIsPhone()` |
 * | Legacy `import { isMobileOnly } from 'react-device-detect'` | OK — aliased here |
 *
 * ## Breakpoints (same as BasicLayout / useResponsive)
 * - `isMobileOnly` / `isMobile` → shell < 1180
 * - `isTablet` → 768–1179
 * - `isDesktop` → ≥ 1180
 *
 * Live `export let` bindings update on resize. React still needs a subscriber
 * (hook/HOC or a parent like BasicLayout) to re-render memoized trees.
 */

import {
  DESKTOP_MIN_WIDTH,
  TABLET_MIN_WIDTH,
  getIsDesktop,
  getIsMobileLayout,
  getIsPhone,
  getIsTablet,
  getWindowWidth,
} from './viewportWidth';

const readMobileShell = () => getIsMobileLayout();
const readTablet = () => getIsTablet();
const readDesktop = () => getIsDesktop();

export let isMobileOnly = readMobileShell();
export let isMobile = readMobileShell();
export let isTablet = readTablet();
export let isDesktop = readDesktop();

const syncFlags = () => {
  isMobileOnly = readMobileShell();
  isMobile = readMobileShell();
  isTablet = readTablet();
  isDesktop = readDesktop();
};

let listening = false;
const ensureListening = () => {
  if (listening || typeof window === 'undefined') return;
  listening = true;

  const onChange = () => syncFlags();
  window.addEventListener('resize', onChange);
  window.addEventListener('orientationchange', onChange);
  if (window.visualViewport) {
    window.visualViewport.addEventListener('resize', onChange);
  }

  [
    window.matchMedia(`(min-width: ${DESKTOP_MIN_WIDTH}px)`),
    window.matchMedia(`(max-width: ${TABLET_MIN_WIDTH - 1}px)`),
  ].forEach((mq) => {
    if (typeof mq.addEventListener === 'function') mq.addEventListener('change', onChange);
    else if (typeof mq.addListener === 'function') mq.addListener(onChange);
  });
};

ensureListening();
syncFlags();

/** Re-read flags when layout width changes without a window resize (e.g. chat dock). */
export const syncDeviceDetectFlags = () => {
  ensureListening();
  syncFlags();
};

export {
  getIsMobileLayout as getIsMobileOnly,
  getIsPhone,
  getIsTablet,
  getIsDesktop,
  getWindowWidth,
};

export default {
  get isMobileOnly() {
    return readMobileShell();
  },
  get isMobile() {
    return readMobileShell();
  },
  get isTablet() {
    return readTablet();
  },
  get isDesktop() {
    return readDesktop();
  },
};
